import type { RootState } from 'src/store';

import { toast } from 'sonner';
import { isEmpty } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import React, { lazy, Suspense, useEffect, useCallback } from 'react';

import { Skeleton } from '@mui/material';

import { paths } from 'src/routes/paths';
import { useRouter, useSearchParams } from 'src/routes/hooks';

import { getConfig } from 'src/global-config';
import { MainLayout } from 'src/layouts/main/layout';
import { getCountry } from 'src/pages/jobs/organisms/helper';
import { setJobStatus, setAllJobProperties, clearCompareCandidates } from 'src/store/Actions/jobEditorActions';

import JobMenuSkeleton from 'src/components/job/jobMenuSkeleton';

import { MainLayoutLeftArea } from './components/main-layout-left-area';
import { MainLayoutRightArea } from './components/main-layout-right-area';
import {
  useJobPostingDetails,
  useJobWorkflowStages,
  useJobWorkflowDetails,
} from './use-job-posting-details';

import type { JobNavLayoutProps } from './types';

const JobActionsMenu = lazy(() =>
  import('./components/job-actions-menu').then((module) => ({ default: module.JobActionsMenu }))
);

export function JobNavLayout({
  children,
  isDeleteLoading,
  isDuplicateLoading,
  updateJobDetails,
  openJobShareModal,
  duplicateJob,
  deleteJob,
}: JobNavLayoutProps) {
  const router = useRouter();
  const params = useSearchParams();
  const JOB_ID = params.get('jobId') || '';
  const dispatch = useDispatch();
  const { jobTitle, jobCountryName, jobCountryId, jobMode, jobCity, jobStatus, jobSlug, orgSlug } =
    useSelector((state: RootState) => state.jobEditor);
  const CONFIG = getConfig();

  const { data: jobDetailsData, isLoading, mutate } = useJobPostingDetails(JOB_ID || null);

  useJobWorkflowStages(JOB_ID || null);
  const { data: workflowDetails } = useJobWorkflowDetails(JOB_ID || null);

  useEffect(() => {
    dispatch(setAllJobProperties({ isPlanDraft: null }));
    dispatch(clearCompareCandidates());
  }, [JOB_ID, dispatch]);

  useEffect(() => {
    if (!isEmpty(jobDetailsData)) {
      dispatch(
        setAllJobProperties({
          jobTitle: jobDetailsData?.Title ?? '-',
          jobCountryName: jobDetailsData?.LocationCountry ?? '',
          jobCountryId: getCountry(jobDetailsData?.LocationCountry).code ?? '',
          jobMode: jobDetailsData?.JobMode ?? '',
          jobCity: jobDetailsData?.LocationCity ?? '',
          jobStatus: jobDetailsData?.Status ?? 'Draft',
          jobSlug: jobDetailsData?.Slug ?? '',
          orgSlug: jobDetailsData?.OrganizationSlug ?? '',
        })
      );
    }
  }, [jobDetailsData, dispatch]);

  useEffect(() => {
    if (workflowDetails !== undefined) {
      dispatch(
        setAllJobProperties({ isPlanDraft: workflowDetails?.HasDraft ?? false })
      );
    }
  }, [workflowDetails, dispatch]);

  const handleShareClick = useCallback(() => {
    if (openJobShareModal && jobDetailsData?.Slug) {
      openJobShareModal({ jobSlug: jobDetailsData.Slug, isPublic: false });
    }
  }, [openJobShareModal, jobDetailsData?.Slug]);

  const onViewLivePage = useCallback(() => {
    if (orgSlug && jobSlug) {
      window.open(`${CONFIG.develop.candidateJobPortalUrl}/${orgSlug}/job/${jobSlug}`, '_blank');
    } else {
      console.error('No orgSlug or jobSlug');
    }
  }, [orgSlug, jobSlug, CONFIG.develop.candidateJobPortalUrl]);

  const updateJobStatus = useCallback(
    (newStatus: string) => {
      updateJobDetails(JOB_ID, { Status: newStatus }, (data: any, success: boolean) => {
        if (success) {
          dispatch(setJobStatus(newStatus));
          void mutate();
          toast.success('Success', {
            description: 'Status updated successfully',
          });
        } else {
          toast.error('Failed', {
            description: 'Status update failed',
          });
        }
      });
    },
    [JOB_ID, updateJobDetails, dispatch, mutate]
  );

  const duplicateCallback = useCallback((resdata: any, success: boolean) => {
    if (success) {
      toast.success('Success', {
        description: 'Job successfully duplicated and saved to Drafts',
      });
    } else {
      toast.error('Failed', {
        description: 'Failed to duplicate job',
      });
    }
  }, []);

  const deleteCallback = useCallback(
    (resdata: any, success: boolean) => {
      if (success) {
        router.push(paths.jobs.root);
        toast.success('Success', {
          description: 'Job successfully deleted.',
        });
      } else {
        toast.error('Failed', {
          description: 'Failed to delete job',
        });
      }
    },
    [router]
  );

  if (JOB_ID && isLoading) {
    return <JobMenuSkeleton />;
  }

  return (
    <MainLayout
      slotProps={{
        nav: { data: [] },
        header: {
          collapsible: true,
          collapseThreshold: 150,
          showThreshold: 100,
          slots: {
            leftArea: (
              <MainLayoutLeftArea
                title={jobTitle}
                country={jobCountryName}
                city={jobCity}
                jobMode={jobMode}
                countryDetails={{ label: jobCountryName, id: jobCountryId }}
              />
            ),
            topArea: null,
            rightArea: (
              <Suspense fallback={<Skeleton variant="rectangular" width={200} height={40} />}>
                <JobActionsMenu
                  jobId={JOB_ID}
                  jobTitle={jobDetailsData?.Title ?? '-'}
                  jobStatus={jobStatus}
                  isDeleteLoading={isDeleteLoading}
                  isDuplicateLoading={isDuplicateLoading}
                  onViewLivePage={onViewLivePage}
                  onStatusChange={updateJobStatus}
                  onShareClick={handleShareClick}
                  onDuplicate={duplicateJob}
                  onDelete={deleteJob}
                  duplicateCallback={duplicateCallback}
                  deleteCallback={deleteCallback}
                  MainLayoutRightArea={MainLayoutRightArea}
                />
              </Suspense>
            ),
            bottomArea: null,
          },
          slotProps: {
            container: {
              sx: {
                '&.MuiContainer-root': {
                  maxWidth: '100%',
                },
              },
            },
          },
        },
      }}
      disableFooter
    >
      {children}
    </MainLayout>
  );
}
